import React, { memo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { digitalArtworks } from "../data/digitalArtworks";
import type { DigitalArtwork } from "../data/digitalArtworks";
import { canvasArtworks } from "../data/canvasArtworks";
import { aiArtworks } from "../data/aiArtworks";
import { useCanvasArtwork, useDigitalArtwork } from "../hooks/useTranslatedArtwork";
import { useAiArtwork } from "../hooks/useAiArtwork";
import type { HomepageEditorialPick } from "../data/homepagePicks";
import type { ArtworkMedium } from "../lib/artworkPaths";
import { artworkPagePath } from "../lib/artworkPaths";
import { isCanvasSold } from "../lib/canvasAvailability";
import { ArtworkImage } from "./ArtworkImage";
import { ArtworkCardGallery } from "./ArtworkImageGallery";
import type { ArtworkGalleryImage } from "./ArtworkImageGallery";
import { InquireArtworkLink } from "./InquireArtworkLink";
import { ArtworkShareButton } from "./ArtworkShareButton";
import { ArtworkExternalLinks } from "./ArtworkExternalLinks";
import { CanvasSoldLabel } from "./CanvasSoldLabel";
import type { SocialLink } from "../types/ArtWork";

type SectionContent = {
  medium: ArtworkMedium;
  artworkId: string;
  artworkName: string;
  mediumLabel: string;
  title: string;
  description?: string;
  imageUrl: string;
  imageAlt: string;
  galleryImages?: ArtworkGalleryImage[];
  externalLinks?: SocialLink[];
  sold?: boolean;
};

function HomepageEditorialSectionShell({
  content,
  index,
  onOpenLightbox,
}: {
  content: SectionContent;
  index: number;
  onOpenLightbox: () => void;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const imageRight = index % 2 === 1;
  const longDescription = (content.description?.length ?? 0) > 320;

  return (
    <article
      className={`border-t border-canvas-200/70 py-12 sm:py-16 md:py-20 ${
        index % 2 === 0 ? "bg-white" : "bg-canvas-50/60"
      }`}
    >
      <div className="section-container">
        <div className="grid items-center gap-6 sm:gap-10 lg:grid-cols-2 lg:gap-14 xl:gap-20">
          <div
            className={`min-w-0 overflow-hidden rounded-2xl bg-canvas-100 p-3 sm:p-6 lg:p-8 ${
              imageRight ? "lg:order-2" : ""
            }`}
          >
            {content.galleryImages && content.galleryImages.length > 0 ? (
              <button
                type="button"
                onClick={onOpenLightbox}
                className="group w-full border-0 bg-transparent p-0 text-start"
              >
                <ArtworkCardGallery
                  images={content.galleryImages}
                  aspectClass="aspect-[4/5] sm:aspect-[3/4]"
                  objectFit="contain"
                  imgClassName="transition-transform duration-700 group-hover:scale-[1.01]"
                />
              </button>
            ) : (
              <button
                type="button"
                onClick={onOpenLightbox}
                className="group w-full border-0 bg-transparent p-0 text-start"
              >
                <ArtworkImage
                  src={content.imageUrl}
                  alt={content.imageAlt}
                  layout="masonry"
                  imgClassName="transition-transform duration-700 group-hover:scale-[1.01]"
                />
              </button>
            )}
          </div>

          <div
            className={`flex flex-col justify-center ${
              imageRight ? "lg:order-1" : ""
            }`}
          >
            <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-accent">
              {content.mediumLabel}
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 sm:mt-4">
              <h3 className="font-display text-2xl font-light leading-tight text-canvas-950 sm:text-4xl md:text-[2.5rem]">
                {content.title}
              </h3>
              {content.sold && <CanvasSoldLabel />}
            </div>
            {content.description && (
              <div className="mt-5 sm:mt-6">
                <p
                  className={`text-sm leading-relaxed text-canvas-600 sm:text-base ${
                    !expanded && longDescription ? "line-clamp-5" : ""
                  }`}
                >
                  {content.description}
                </p>
                {longDescription && !expanded && (
                  <button
                    type="button"
                    onClick={() => setExpanded(true)}
                    className="mt-3 border-0 bg-transparent p-0 text-xs font-medium uppercase tracking-[0.14em] text-canvas-500 transition hover:text-accent"
                  >
                    {t("common.readMore")}
                  </button>
                )}
              </div>
            )}
            <ArtworkExternalLinks
              links={content.externalLinks}
              className="mt-6 sm:mt-7"
            />
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Link
                to={artworkPagePath(content.medium, content.artworkId)}
                className="btn-accent text-center !text-xs sm:!text-sm"
              >
                {t("artwork.viewPiece")}
              </Link>
              <ArtworkShareButton
                medium={content.medium}
                id={content.artworkId}
                title={content.title}
                text={content.description}
                className="btn-secondary text-center !text-xs sm:!text-sm"
              />
              {content.sold ? (
                <Link
                  to="/contact#commission"
                  className="btn-secondary text-center !text-xs sm:!text-sm"
                >
                  {t("canvas.sold.cta")}
                </Link>
              ) : (
                <InquireArtworkLink
                  medium={content.medium}
                  id={content.artworkId}
                  name={content.artworkName}
                  className="btn-secondary text-center !text-xs sm:!text-sm"
                />
              )}
            </div>
            <button
              type="button"
              onClick={onOpenLightbox}
              className="mt-5 border-0 bg-transparent p-0 text-start text-sm font-medium text-accent transition hover:text-accent-dark"
            >
              {t("artwork.tapToView")} →
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}

function mediumLabelKey(medium: ArtworkMedium): string {
  return `home.featured.mediums.${medium}`;
}

function CanvasEditorialSection({
  work,
  index,
  onOpenLightbox,
}: {
  work: (typeof canvasArtworks)[number];
  index: number;
  onOpenLightbox: () => void;
}) {
  const { t } = useTranslation();
  const translated = useCanvasArtwork(work);
  return (
    <HomepageEditorialSectionShell
      index={index}
      onOpenLightbox={onOpenLightbox}
      content={{
        medium: "canvas",
        artworkId: work.id,
        artworkName: translated.name,
        mediumLabel: t(mediumLabelKey("canvas")),
        title: translated.title,
        description: translated.description,
        imageUrl: translated.imageUrl,
        imageAlt: translated.name,
        galleryImages: translated.galleryImages,
        sold: isCanvasSold(work),
      }}
    />
  );
}

function AiEditorialSection({
  work,
  index,
  onOpenLightbox,
}: {
  work: (typeof aiArtworks)[number];
  index: number;
  onOpenLightbox: () => void;
}) {
  const { t } = useTranslation();
  const translated = useAiArtwork(work);
  return (
    <HomepageEditorialSectionShell
      index={index}
      onOpenLightbox={onOpenLightbox}
      content={{
        medium: "ai",
        artworkId: work.id,
        artworkName: translated.name,
        mediumLabel: t(mediumLabelKey("ai")),
        title: translated.title,
        description: translated.description,
        imageUrl: translated.imageUrl,
        imageAlt: translated.name,
      }}
    />
  );
}

function DigitalEditorialSection({
  work,
  index,
  onOpenLightbox,
}: {
  work: DigitalArtwork;
  index: number;
  onOpenLightbox: () => void;
}) {
  const { t } = useTranslation();
  const translated = useDigitalArtwork(work);
  return (
    <HomepageEditorialSectionShell
      index={index}
      onOpenLightbox={onOpenLightbox}
      content={{
        medium: "digital",
        artworkId: work.id,
        artworkName: translated.name,
        mediumLabel: t(mediumLabelKey("digital")),
        title: translated.title,
        description: translated.description,
        imageUrl: translated.imageUrl,
        imageAlt: translated.name,
        externalLinks: work.links,
      }}
    />
  );
}

export const EditorialPickCard = memo(function EditorialPickCard({
  pick,
  index,
  onClick,
}: {
  pick: HomepageEditorialPick;
  index: number;
  onClick: () => void;
}) {
  if (pick.medium === "canvas") {
    const work = canvasArtworks.find((w) => w.id === pick.id);
    if (!work) return null;
    return (
      <CanvasEditorialSection
        work={work}
        index={index}
        onOpenLightbox={onClick}
      />
    );
  }
  if (pick.medium === "ai") {
    const work = aiArtworks.find((w) => w.id === pick.id);
    if (!work) return null;
    return (
      <AiEditorialSection
        work={work}
        index={index}
        onOpenLightbox={onClick}
      />
    );
  }
  const work = digitalArtworks.find((w) => w.id === pick.id);
  if (!work) return null;
  return (
    <DigitalEditorialSection
      work={work}
      index={index}
      onOpenLightbox={onClick}
    />
  );
});
